/**
 * Twenty Faces — dealing, scoring and wording.
 *
 * Kept out of the component so a board can be dealt and solved without a DOM,
 * and so `scripts/check-faces.mjs` can play thousands of games against the same
 * code the player does.
 */

import { CHARACTERS } from '../data/characters.js';
import { BOARD_SIZE, FACE_QUESTIONS } from '../data/faces.js';

/** Fisher–Yates. `sort(() => Math.random() - 0.5)` is not a shuffle. */
function shuffled(list) {
  const out = list.slice();
  for (let i = out.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

/**
 * Deal a board and pick the face to find.
 *
 * Twenty of thirty, dealt fresh each time. Dealing a subset rather than showing
 * all thirty is what gives the mode its replay value: the same character is a
 * different problem depending on who else is on the board, because the
 * questions that separate them change.
 *
 * Every one of the thirty has a distinct set of answers, so any subset of them
 * does too, and the board is always solvable without guessing.
 */
export function dealBoard(pool = CHARACTERS, size = BOARD_SIZE) {
  const faces = shuffled(pool).slice(0, size);
  const secret = faces[Math.floor(Math.random() * faces.length)];
  return { faces, secretId: secret.id };
}

/** Everyone still standing after a question is answered about the secret. */
export function survivors(faces, question, answer) {
  return faces.filter((c) => question.test(c) === answer);
}

/**
 * What a solved board is worth.
 *
 * Questions are the currency. Perfect play on twenty faces is four or five
 * questions, so the numbers are set from that rather than from a round figure:
 * a clean four-question win is around 480, and a player who asks everything and
 * still gets there keeps something for getting there.
 *
 * A wrong guess costs more than three questions. It has to — otherwise
 * guessing early and often beats narrowing the board down, and the game stops
 * being about the questions.
 */
export function scoreFaces(questions, wrongGuesses) {
  return Math.max(60, 700 - questions * 55 - wrongGuesses * 180);
}

const RANKS = [
  { at: 500, name: 'You knew who you were looking for' },
  { at: 410, name: 'Sharp questions' },
  { at: 320, name: 'Narrowed it down' },
  { at: 200, name: 'Worked it out' },
  { at: 0, name: 'Got there in the end' },
];

export function rankFaces(score) {
  return RANKS.find((rank) => score >= rank.at).name;
}

/** Something worth pasting somewhere. */
export function shareFaces(name, questions, wrongGuesses, score) {
  return [
    `Twenty Faces — ${name} in ${questions} question${questions === 1 ? '' : 's'}`,
    '🔎'.repeat(Math.min(questions, 12)) + '❌'.repeat(Math.min(wrongGuesses, 6)),
    `${score} · ${rankFaces(score)}`,
    'scripturejourney.net',
  ].join('\n');
}

export { BOARD_SIZE, FACE_QUESTIONS };
